import { useEffect, useId, useRef, useState } from 'react';
import { adminAuthApi } from '@/features/auth/api/adminAuthApi';
import { passwordChangeErrors } from '@/features/auth/adminAuth';
import { PasswordField } from '@/features/auth/PasswordField';
import type { PasswordChangeErrors } from '@/features/auth/types/PasswordChangeErrors';
import type { PasswordChangeValues } from '@/features/auth/types/PasswordChangeValues';
import { useDocumentTitle } from '@/features/common/useDocumentTitle';
import { adminAuth } from '@/i18n/dictionaries/adminAuth';
import { useTranslation } from '@/i18n/useTranslation';
import { detailOr } from '@/services/api';
import { consumeReturnTo } from '@/services/reauth';
import '@/features/auth/admin-auth.css';

const EMPTY: PasswordChangeValues = { currentPassword: '', newPassword: '', confirmation: '' };

/**
 * /admin/parola — changing the administration password (ADM-1): the one a temporary password has to
 * go through before any administration route answers, and where every one of those refusals sends
 * the tab. The policy — twelve characters, not the username — is checked here and again by the
 * server, whose refusal is shown under the form in its words.
 */
export function AdminPasswordPage() {
  const { t } = useTranslation(adminAuth);
  useDocumentTitle(t('passwordTitle'));
  const currentId = useId();
  const newId = useId();
  const confirmationId = useId();
  const currentRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef<HTMLParagraphElement>(null);
  const [values, setValues] = useState<PasswordChangeValues>(EMPTY);
  const [errors, setErrors] = useState<PasswordChangeErrors>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Where "Mergi la cabinet" leads, read once when the change succeeds: consumeReturnTo forgets
  // the target as it hands it over, so reading it while rendering would lose it on the next render.
  const [done, setDone] = useState<string | null>(null);
  const set = (patch: Partial<PasswordChangeValues>) => setValues((v) => ({ ...v, ...patch }));

  useEffect(() => currentRef.current?.focus(), []);
  useEffect(() => {
    if (done) doneRef.current?.focus();
  }, [done]);

  const submit = async () => {
    const found = passwordChangeErrors(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    setRefusal(null);
    try {
      await adminAuthApi.changePassword(values.currentPassword, values.newPassword);
    } catch (e) {
      setRefusal(detailOr(e, t('changeFailed')));
      setBusy(false);
      return;
    }
    setValues(EMPTY);
    setBusy(false);
    setDone(consumeReturnTo() ?? '/app/cabinet');
  };

  return (
    <main className="admin-auth">
      <div className="admin-auth__wrap">
        <div className="admin-auth__card card">
          <div className="admin-auth__brand" aria-hidden="true">
            <span className="brand__mark">
              <span className="brand__mark-text">AK</span>
            </span>
            <span className="admin-auth__word">AKIS</span>
          </div>
          <h1>{t('passwordTitle')}</h1>
          {done ? (
            <>
              <p className="admin-auth__done" role="status" tabIndex={-1} ref={doneRef}>
                {t('passwordChanged')}
              </p>
              <a className="btn btn--primary btn--md" href={done}>
                {t('toCabinet')}
              </a>
            </>
          ) : (
            <>
              <p className="admin-auth__lead">{t('passwordLead')}</p>
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  void submit();
                }}
              >
                <PasswordField
                  id={currentId}
                  label={t('currentPassword')}
                  value={values.currentPassword}
                  onChange={(currentPassword) => set({ currentPassword })}
                  autoComplete="current-password"
                  inputRef={currentRef}
                  error={errors.currentPassword ? t(errors.currentPassword) : undefined}
                />
                <PasswordField
                  id={newId}
                  label={t('newPassword')}
                  value={values.newPassword}
                  onChange={(newPassword) => set({ newPassword })}
                  autoComplete="new-password"
                  hint={t('passwordHint')}
                  error={errors.newPassword ? t(errors.newPassword) : undefined}
                />
                <PasswordField
                  id={confirmationId}
                  label={t('confirmation')}
                  value={values.confirmation}
                  onChange={(confirmation) => set({ confirmation })}
                  autoComplete="new-password"
                  error={errors.confirmation ? t(errors.confirmation) : undefined}
                />
                {refusal && (
                  <p className="message message--inline message--error" role="alert">
                    {refusal}
                  </p>
                )}
                <button
                  type="submit"
                  className="btn btn--primary btn--md admin-auth__submit"
                  disabled={busy}
                >
                  {busy ? t('changingPassword') : t('changePassword')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
